import React, { useEffect, useState } from 'react';
import {
    AlignLeft,
    ArrowDownUp,
    Check,
    CheckCircle2,
    CircleOff,
    Eye,
    EyeOff,
    Gem,
    Image as ImageIcon,
    ImageOff,
    Layers3,
    Link2,
    Palette,
    PencilLine,
    Plus,
    Sparkles,
    Trash2,
    Type,
    UploadCloud,
    X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import SafeImage from '../../components/SafeImage';
import Select from '../../components/Select';
import ConfirmDialog from '../../components/ConfirmDialog';
import API from '../../utils/api';
import { RULES, checkFiles, limitNote, uploadFailMessage } from '../../utils/uploadRules';
import './ServiceCards.css';

const SERVER_URL = (API.defaults?.baseURL || process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
const imgUrl = path => (path && path.startsWith('/') ? `${SERVER_URL}${path}` : path);

const PAGE_SIZE = 10;
const GLOW_COLORS = ['#DF9B08', '#C97B2E', '#8FAE5A', '#B5471B', '#C9668E', '#7B5CC9'];
const EMPTY_FORM = { name: '', description: '', glow: '#DF9B08', order: 0, category: '' };

const ServiceCards = () => {
    const [cards, setCards] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [saving, setSaving] = useState(false);
    const [serviceCategories, setServiceCategories] = useState([]);
    const [pendingDelete, setPendingDelete] = useState(null);

    const [total, setTotal] = useState(0);
    const [activeCount, setActiveCount] = useState(0);
    const [linkedCategories, setLinkedCategories] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    const usedCategories = new Set(linkedCategories);
    const categoryOptions = serviceCategories.map(category => ({
        value: category,
        label: usedCategories.has(category) ? `${category} · already linked` : category,
    }));

    useEffect(() => {
        API.get('/admin/options', { params: { category: 'servicecategory', status: 'active' } })
            .then(response => setServiceCategories((response.data.options || []).map(option => option.value)))
            .catch(() => setServiceCategories([]));
    }, []);

    useEffect(() => { fetchCards(); }, []);

    useEffect(() => () => {
        if (imagePreview.startsWith('blob:')) URL.revokeObjectURL(imagePreview);
    }, [imagePreview]);

    // Loads the first page, or (after an edit/toggle/delete) reloads everything
    // already shown so the user's place in the list is kept.
    const fetchCards = async (reloadCount = PAGE_SIZE) => {
        try {
            const response = await API.get('/service-cards/all', {
                params: { limit: Math.max(reloadCount, PAGE_SIZE), skip: 0 },
            });
            applyPage(response.data, false);
        } catch (error) {
            toast.error('Failed to load service cards');
        }
    };

    const applyPage = (data, append) => {
        setCards(current => append ? [...current, ...(data.cards || [])] : (data.cards || []));
        setTotal(data.total || 0);
        setActiveCount(data.activeCount || 0);
        setLinkedCategories(data.usedCategories || []);
        setHasMore(Boolean(data.hasMore));
    };

    const loadMore = async () => {
        setLoadingMore(true);
        try {
            const response = await API.get('/service-cards/all', { params: { limit: PAGE_SIZE, skip: cards.length } });
            applyPage(response.data, true);
        } catch (error) {
            toast.error('Failed to load more service cards');
        } finally {
            setLoadingMore(false);
        }
    };

    const refreshCards = () => fetchCards(cards.length);

    const updateForm = (field, value) => setForm(current => ({ ...current, [field]: value }));

    const resetForm = () => {
        setForm(EMPTY_FORM);
        setImageFile(null);
        setImagePreview('');
        setEditingId(null);
        setShowForm(false);
    };

    const handleImageSelect = event => {
        const file = event.target.files[0];
        if (!file) return;
        if (!checkFiles([file], RULES.photo).length) {
            event.target.value = '';
            return;
        }
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
        event.target.value = '';
    };

    const clearSelectedImage = () => {
        setImageFile(null);
        setImagePreview('');
    };

    const startEdit = card => {
        setForm({
            name: card.name,
            description: card.description || '',
            glow: card.glow || '#DF9B08',
            order: card.order || 0,
            category: card.category || '',
        });
        setImageFile(null);
        setImagePreview(card.image ? imgUrl(card.image) : '');
        setEditingId(card._id);
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = async event => {
        event.preventDefault();
        setSaving(true);
        try {
            const data = new FormData();
            data.append('name', form.name);
            data.append('description', form.description);
            data.append('glow', form.glow);
            data.append('order', form.order);
            data.append('category', form.category);
            if (imageFile) data.append('image', imageFile);

            if (editingId) {
                await API.put(`/service-cards/${editingId}`, data, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                toast.success('Service card updated!');
            } else {
                await API.post('/service-cards', data, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                toast.success('Service card created!');
            }
            resetForm();
            refreshCards();
        } catch (error) {
            toast.error(imageFile && (error.response?.status === 413 || !error.response) ? uploadFailMessage(error, [imageFile], RULES.photo) : (error.response?.data?.message || 'Failed to save service card'));
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = async id => {
        try {
            const response = await API.put(`/service-cards/${id}/toggle`);
            toast.success(response.data.message);
            refreshCards();
        } catch (error) {
            toast.error('Failed to update service card');
        }
    };

    const confirmDeleteCard = async () => {
        const id = pendingDelete;
        if (!id) return;
        setPendingDelete(null);
        try {
            await API.delete(`/service-cards/${id}`);
            toast.success('Service card deleted!');
            refreshCards();
        } catch (error) {
            toast.error('Failed to delete service card');
        }
    };

    return (
        <div className="service-cards-layout">
            <Sidebar />
            <div className="admin-content service-cards-content">
                <Navbar title={<span className="service-cards-nav-title"><Gem size={18} /> Service cards</span>} />

                <main className="service-cards-page">
                    <header className="service-cards-hero">
                        <div className="service-cards-hero__copy">
                            <span className="service-cards-eyebrow"><Sparkles size={14} /> Wedding service showcase</span>
                            <h1>Homepage service cards</h1>
                            <p>Curate the wedding services members discover on your homepage and connect each card to its matching service category.</p>
                        </div>
                        <div className="service-cards-hero__stats">
                            <div><strong>{total}</strong><span>Total cards</span></div>
                            <div><strong>{activeCount}</strong><span>Active now</span></div>
                            <div className="service-cards-format"><ImageIcon size={19} /><span><b>Landscape image</b>600 × 460 or larger</span></div>
                        </div>
                    </header>

                    <section className="service-cards-toolbar">
                        <div>
                            <h2>Service card library</h2>
                            <p>{total} {total === 1 ? 'card' : 'cards'} configured for the homepage</p>
                        </div>
                        <button type="button" className={showForm ? 'is-cancel' : ''} onClick={() => showForm ? resetForm() : setShowForm(true)}>
                            {showForm ? <><X size={17} /> Close editor</> : <><Plus size={17} /> Add service card</>}
                        </button>
                    </section>

                    {showForm && (
                        <form className="service-card-form" onSubmit={handleSubmit}>
                            <header className="service-card-form__header">
                                <span><PencilLine size={19} /></span>
                                <div>
                                    <h2>{editingId ? 'Edit service card' : 'Create a service card'}</h2>
                                    <p>Set the artwork, destination, display order and visual accent.</p>
                                </div>
                            </header>

                            <div className="service-card-form__body">
                                <section className="service-card-form__media">
                                    <div className="service-card-form__label"><ImageIcon size={14} /> Card artwork <span>JPG or PNG · Max 5MB</span></div>
                                    {imagePreview ? (
                                        <div className="service-card-image-preview" style={{ '--card-glow': form.glow }}>
                                            <div className="service-card-image-preview__hex">
                                                <SafeImage src={imagePreview} alt="Service card preview" />
                                            </div>
                                            <div>
                                                <strong>Homepage preview</strong>
                                                <p>Your image fills the top of the card, cropped to a wide frame.</p>
                                                <small className="upload-limit"><b>Limit:</b> {limitNote(RULES.photo)}</small>
                                                <label className="service-card-image-preview__change">
                                                    <UploadCloud size={14} /> Replace artwork
                                                    <input type="file" accept="image/*" onChange={handleImageSelect} />
                                                </label>
                                            </div>
                                            {imageFile && (
                                                <button type="button" className="service-card-image-preview__remove" onClick={clearSelectedImage} aria-label="Remove selected artwork">
                                                    <Trash2 size={15} />
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <label className="service-card-upload">
                                            <input type="file" accept="image/*" onChange={handleImageSelect} />
                                            <span><UploadCloud size={28} /></span>
                                            <strong>Upload card image</strong>
                                            <p>Use a landscape photo with the subject near the centre; it is cropped to a wide frame.</p>
                                            <small className="upload-limit"><b>Limit:</b> {limitNote(RULES.photo)}</small>
                                            <small>Browse files</small>
                                        </label>
                                    )}
                                </section>

                                <section className="service-card-form__fields">
                                    <ServiceCardField icon={Type} label="Card name" required>
                                        <input value={form.name} onChange={event => updateForm('name', event.target.value)} placeholder="Example: Wedding halls" required />
                                    </ServiceCardField>

                                    <ServiceCardField icon={ArrowDownUp} label="Display order">
                                        <input type="number" value={form.order} onChange={event => updateForm('order', event.target.value)} placeholder="0" />
                                    </ServiceCardField>

                                    <ServiceCardField icon={Link2} label="Linked service category" wide>
                                        <Select
                                            value={form.category}
                                            onChange={value => updateForm('category', value)}
                                            options={categoryOptions}
                                            placeholder="All services (no filter)"
                                            ariaLabel="Linked service category"
                                        />
                                        <small>Choose where “Learn more” should take the member. Categories marked “already linked” are used by another card.</small>
                                    </ServiceCardField>

                                    <ServiceCardField icon={Palette} label="Accent glow" wide>
                                        <div className="service-card-swatches" role="group" aria-label="Accent glow color">
                                            {GLOW_COLORS.map(color => (
                                                <button
                                                    type="button"
                                                    key={color}
                                                    className={form.glow === color ? 'is-selected' : ''}
                                                    style={{ '--swatch': color }}
                                                    onClick={() => updateForm('glow', color)}
                                                    aria-label={`Use ${color} accent color`}
                                                    aria-pressed={form.glow === color}
                                                >
                                                    {form.glow === color && <Check size={14} />}
                                                </button>
                                            ))}
                                            <label className={`service-card-custom ${GLOW_COLORS.includes(form.glow) ? '' : 'is-selected'}`} title="Pick any colour">
                                                <input type="color" value={/^#[0-9a-f]{6}$/i.test(form.glow) ? form.glow : '#DF9B08'} onChange={event => updateForm('glow', event.target.value.toUpperCase())} aria-label="Custom accent colour" />
                                                <Plus size={14} />
                                            </label>
                                            <input
                                                className="service-card-hex"
                                                value={form.glow}
                                                maxLength={7}
                                                spellCheck={false}
                                                onChange={event => { const v = event.target.value.trim(); updateForm('glow', v.startsWith('#') ? v : '#' + v); }}
                                                aria-label="Accent colour hex code"
                                            />
                                        </div>
                                    </ServiceCardField>

                                    <ServiceCardField icon={AlignLeft} label="Description" wide>
                                        <textarea value={form.description} onChange={event => updateForm('description', event.target.value)} placeholder="Short description shown on the card" />
                                    </ServiceCardField>
                                </section>
                            </div>

                            <footer className="service-card-form__footer">
                                <span><CheckCircle2 size={15} /> New cards appear immediately after they are created.</span>
                                <div>
                                    <button type="button" className="secondary" onClick={resetForm}>Cancel</button>
                                    <button type="submit" className="primary" disabled={saving}>
                                        {editingId ? <PencilLine size={15} /> : <Plus size={16} />}
                                        {saving ? 'Saving…' : editingId ? 'Save changes' : 'Create card'}
                                    </button>
                                </div>
                            </footer>
                        </form>
                    )}

                    {cards.length > 0 ? (
                        <>
                        <section className="service-cards-grid" aria-label="Configured service cards">
                            {cards.map(card => (
                                <article key={card._id} className={`service-card-admin ${card.isActive ? '' : 'is-inactive'}`} style={{ '--card-glow': card.glow || '#DF9B08' }}>
                                    <div className="service-card-admin__visual">
                                        <span className="service-card-admin__order"><ArrowDownUp size={12} /> Order {card.order}</span>
                                        <span className={`service-card-admin__status ${card.isActive ? 'is-active' : ''}`}>
                                            {card.isActive ? <CheckCircle2 size={12} /> : <CircleOff size={12} />}
                                            {card.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                        <div className="service-card-admin__hex">
                                            {card.image
                                                ? <SafeImage src={imgUrl(card.image)} alt={card.name} />
                                                : <div className="service-card-admin__placeholder"><ImageOff size={24} /><span>No artwork</span></div>}
                                        </div>
                                    </div>

                                    <div className="service-card-admin__body">
                                        <div className="service-card-admin__heading">
                                            <div><span className="service-card-admin__accent" /><h3>{card.name}</h3></div>
                                            {card.category && <span className="service-card-admin__category"><Link2 size={11} /> {card.category}</span>}
                                        </div>
                                        <p>{card.description || 'No description provided.'}</p>

                                        <div className="service-card-admin__actions">
                                            <button type="button" className="edit" onClick={() => startEdit(card)}><PencilLine size={14} /> Edit</button>
                                            <button type="button" className="toggle" onClick={() => handleToggle(card._id)}>
                                                {card.isActive ? <><EyeOff size={14} /> Hide</> : <><Eye size={14} /> Show</>}
                                            </button>
                                            <button type="button" className="delete" onClick={() => setPendingDelete(card._id)} aria-label={`Delete ${card.name}`}><Trash2 size={14} /></button>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </section>
                        {hasMore && (
                            <div className="service-cards-more">
                                <button type="button" onClick={loadMore} disabled={loadingMore}>
                                    {loadingMore ? 'Loading…' : `Load more (${total - cards.length} remaining)`}
                                </button>
                            </div>
                        )}
                        </>
                    ) : !showForm && (
                        <section className="service-cards-empty">
                            <span><Layers3 size={31} /></span>
                            <h2>No service cards yet</h2>
                            <p>Create your first card to replace the homepage’s built-in wedding service suggestions.</p>
                            <button type="button" onClick={() => setShowForm(true)}><Plus size={16} /> Create first card</button>
                        </section>
                    )}
                </main>
            </div>

            <ConfirmDialog
                open={Boolean(pendingDelete)}
                title="Delete service card?"
                message="This card will no longer appear on the homepage."
                confirmLabel="Delete card"
                danger
                onConfirm={confirmDeleteCard}
                onCancel={() => setPendingDelete(null)}
            />
        </div>
    );
};

const ServiceCardField = ({ icon: Icon, label, required, wide, children }) => (
    <div className={`service-card-field${wide ? ' is-wide' : ''}`}>
        <span><Icon size={13} /> {label}{required && <b>*</b>}</span>
        {children}
    </div>
);

export default ServiceCards;
